import { defineStore } from 'pinia';
import { type Arguments, type LibError, type Payment, type Results } from '~/types/paymentsApi';
import { useStoreRoot } from '../abstract/root';
import { useStorePaymentSystemSorting } from '../strapi/paymentSystemSorting';
import { useStoreSsPlayer } from './player';
import type { PaymentMethodFiatExtendedInterface } from '~/types/paymentMethodFiatExtended.interface';

export const useStoreSsWithdrawMethods = defineStore('ssWithdrawMethods', {
  state: () => ({
    methods: [] as Results.GetMethodsResult,
    selectedMethod: null as Payment.Method | null,
    paymentPlan: null as Results.GetMethodFieldsResult | null,
    paymentError: {} as LibError.SubmitError,
    savePlayerFieldsError: {} as LibError.SavePlayerFieldsError,
  }),

  getters: {
    ssPaymentMethods(): PaymentMethodFiatExtendedInterface[] {
      const sortingGroup = useStorePaymentSystemSorting().withdrawSortingData.find((sorting) =>
        useStoreSsPlayer().playerGroupIds.includes(sorting.groupId)
      );
      return this.methods
        .map((method) => {
          const { brand, provider } = method;
          let currency = method.termsOfService.restrictions.amountCurrencyCode;

          if (method.type === 'crypto') {
            currency = method.id.split('_')[2];
          }

          const position = sortingGroup?.paymentSystems.find((system) => {
            const methodId = (
              provider === 'coinspaid' ? `${brand}_${currency}` : brand
            )?.toLowerCase();
            return system.paymentSystemId === methodId;
          })?.position;

          return {
            ...method,
            position: position || 99,
            methodLogoId: (provider === 'coinspaid'
              ? `${brand}_${currency}`
              : brand
            )?.toLowerCase(),
          };
        })
        .sort((a, b) => a.position - b.position);
    },

    selectedMethodExtended(): PaymentMethodFiatExtendedInterface | null {
      if (this.selectedMethod === null) {
        return null;
      }

      const { brand, provider } = this.selectedMethod;
      let currency = this.selectedMethod.termsOfService.restrictions.amountCurrencyCode;

      if (this.selectedMethod.type === 'crypto') {
        currency = this.selectedMethod.id.split('_')[2];
      }

      return {
        ...this.selectedMethod,
        methodLogoId: (provider === 'coinspaid' ? `${brand}_${currency}` : brand)?.toLowerCase(),
      };
    },

    selectedMethodSavedData(): any[] | null {
      return this.selectedMethodExtended?.savedProfiles || null;
    },

    paymentPlanFields(): string[] {
      const fields: string[] = [];

      this.paymentPlan?.methodFields.forEach((item) => {
        if (item.fieldName) {
          fields.push(item.fieldName);
        }
      });

      return fields;
    },

    selectedMethodType(): string {
      return this.selectedMethod?.type || '';
    },
  },

  actions: {
    async loadConfig() {
      const ssPaymentsApi = window.PaymentsAPI;
      const root = useStoreRoot();

      const data = await ssPaymentsApi?.config({
        language: root.userLang,
        cdnList: ['https://cdn2.softswiss.net', 'https://cdn.softswiss.net/'],
      });

      return data;
    },

    /**
     * Method for loading payments data for selected currency.
     * Payments data were filtering by availability and saved payments methods were adding
     * before saving.
     * @param currency currency for which payment's methods load
     */
    async loadMethods(currency: string): Promise<any> {
      this.methods = [];

      if (!import.meta.browser || !window) {
        return;
      }

      const ssPaymentsApi = window.PaymentsAPI;

      try {
        this.loadConfig();
        const methods = await ssPaymentsApi?.getMethods({
          currency,
          paymentAction: 'cashout' as Payment.Action.Deposit,
        });

        if (!methods) {
          return;
        }

        this.methods = methods;
      } catch (e: any) {
        console.error(e);
      }
    },

    /**
     * Method for loading data for submitting deposit with selected payment method
     * @param currency currency for which payment's methods load
     */
    async loadPaymentPlan(currency: string, methodID: string) {
      if (!import.meta.browser || !window) {
        return;
      }

      this.paymentPlan = null;

      const ssPaymentsApi = window.PaymentsAPI;

      try {
        const paymentPlan = await ssPaymentsApi?.getMethodFields({
          currency,
          id: methodID,
          paymentAction: 'cashout' as Payment.Action.Deposit,
        });

        if (!paymentPlan) {
          return;
        }

        this.paymentPlan = paymentPlan;
      } catch (e: any) {
        console.error(e);
      }
    },

    /**
     * Method for loading data for submitting deposit that used
     * if user want to use saved payment method
     * @param params savedMethod - player's saved payment's data
     * @param params currency
     */
    async loadPaymentPlanSavedMethod(params: {
      savedMethod: { id: string };
      currency: string;
      methodID: string;
    }) {
      if (!import.meta.browser || !window) {
        return;
      }

      const ssPaymentsApi = window.PaymentsAPI;

      try {
        const paymentPlan = await ssPaymentsApi?.getMethodFields({
          currency: params.currency,
          id: params.methodID,
          paymentAction: 'cashout' as Payment.Action.Deposit,
          savedProfileId: params.savedMethod.id,
        });

        if (!paymentPlan) {
          return;
        }

        this.paymentPlan = paymentPlan;
      } catch (e: any) {
        console.error(e);
      }
    },

    /**
       * Method tha submit player's inputed payments data
       * @param ctx context
       * @param params player's fields e.g.: {
       *       amount: '20',
               account: 'hans@magnetics.io',
            }
      * @returns DepositResultType if success; DepositError object if payment failed
      */
    async submitDeposit(
      params: Partial<any>,
      playerFieldsData?: Record<string, string>
    ): Promise<any> {
      if (!import.meta.browser || !window?.location) {
        return;
      }

      try {
        const ssPaymentsApi = window.PaymentsAPI;

        const currency = useStoreSsPlayer().playerCurrency || 'EUR';

        await this.loadConfig();

        if (this.paymentPlan === null) {
          return;
        }

        const submitData: Arguments.SubmitArgs = {
          amountValue: params.amount,
          currency,
          paymentAction: 'cashout' as Payment.Action,
          id: this.selectedMethod?.id || '',
          methodFieldsData: this.paymentPlan.methodFields
            .filter((item) => item.type !== 'banner')
            .reduce((result, item) => {
              if (item?.fieldName) {
                result[item.fieldName] = params[item.fieldName];
              }
              return result;
            }, {} as Record<string, string>),
        };

        if (playerFieldsData) {
          submitData.playerFieldsData = playerFieldsData;
        }

        if (params.savedProfileId) {
          submitData.savedProfileId = params.savedProfileId;
        }

        const submit = await ssPaymentsApi?.submit(submitData);

        if (submit?.status === 'outdated_method_fields') {
          this.loadPaymentPlan(currency, this.selectedMethod?.id || '');
        }
        return submit;
      } catch (error) {
        this.paymentError = error as LibError.SubmitError;
        return error as LibError.SubmitError;
      }
    },

    async savePlayerFields(params: Arguments.SavePlayerFieldsArgs) {
      const data = await window.PaymentsAPI?.savePlayerFields(params);

      return data;
    },

    async deleteSavedProfile(id: string) {
      const currency = useStoreSsPlayer().playerCurrency || 'EUR';

      const data = await window.PaymentsAPI?.deleteSavedProfile({
        currency,
        paymentAction: 'cashout' as Payment.Action,
        id: this.selectedMethod?.id || '',
        savedProfileId: id,
      });

      return data;
    },

    setSelectedMethod(index: number) {
      this.selectedMethod = this.ssPaymentMethods[index];
    },

    resetSelectedMethod() {
      this.selectedMethod = null;
    },

    resetPaymentPlan() {
      this.paymentPlan = null;
    },

    resetErrorState() {
      this.paymentError = {} as LibError.SubmitError;
    },

    resetCache() {
      const ssPaymentsApi = window.PaymentsAPI;

      return ssPaymentsApi?.resetCache();
    },
  },
});
